import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {
    FiAlertCircle,
    FiBookOpen,
    FiCheckCircle,
    FiClock,
    FiCpu,
    FiDatabase,
    FiEdit3,
    FiEye,
    FiFileText,
    FiGitBranch,
    FiLayers,
    FiLoader,
    FiPlay,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiTrash2,
    FiUploadCloud,
    FiX,
} from 'react-icons/fi';
import './ManageResources.css';
import {accountsApi, getErrorMessage, knowledgeGraphApi, resourcesApi, subjectInstructionsApi, topicsApi} from '../services/api';
import useAuthRole from '../hooks/useAuthRole';

const ingestLabels = {
    pending: 'در انتظار پردازش',
    ingesting: 'در حال پردازش',
    ingested: 'پردازش شده',
    failed: 'ناموفق',
};

const graphLabels = {
    pending: 'در انتظار ساخت',
    processing: 'در حال ساخت',
    ready: 'گراف آماده',
    error: 'خطای گراف',
};

const statusFilters = [
    {value: 'all', label: 'همه منابع'},
    {value: 'pending', label: 'در انتظار پردازش'},
    {value: 'ingesting', label: 'در حال پردازش'},
    {value: 'ingested', label: 'پردازش شده'},
    {value: 'failed', label: 'ناموفق'},
];

const defaultSubjectOptions = [
    {subject_type: 'general', title: 'عمومی'},
    {subject_type: 'language', title: 'زبان'},
    {subject_type: 'foreign_language', title: 'زبان خارجی / ESP'},
    {subject_type: 'math', title: 'ریاضی'},
    {subject_type: 'science', title: 'علوم'},
    {subject_type: 'history', title: 'تاریخ'},
];

const normalizeCollection = (data) => Array.isArray(data) ? data : (data?.results || data?.items || []);

const formatFileSize = (size) => {
    if (!size) return 'حجم نامشخص';
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} کیلوبایت`;
    return `${(size / (1024 * 1024)).toFixed(1)} مگابایت`;
};

const getFileExtension = (name = '') => name.split('.').pop()?.toUpperCase() || 'FILE';

const getLlmLogId = (resource, operation) => resource?.llm_log_id
    || resource?.last_llm_log_id
    || resource?.[`${operation}_llm_log_id`]
    || resource?.llm_logs?.[operation]?.id
    || resource?.llm_logs?.find?.((log) => log.operation === operation)?.id
    || null;

const formatEditError = (error) => {
    const message = getErrorMessage(error);
    if (error?.data?.code !== 'course_change_conflict') return message;
    const questionIds = error.data.dependencies?.question_ids || [];
    const examIds = error.data.dependencies?.exam_ids || [];
    const dependencies = [
        questionIds.length ? `سؤال‌ها: ${questionIds.join('، ')}` : '',
        examIds.length ? `آزمون‌ها: ${examIds.join('، ')}` : '',
    ].filter(Boolean).join(' | ');
    return dependencies ? `${message} وابستگی‌های مانع انتقال — ${dependencies}` : message;
};

const StatusBadge = ({status}) => {
    const Icon = status === 'ingested'
        ? FiCheckCircle
        : status === 'failed'
            ? FiAlertCircle
            : status === 'ingesting'
                ? FiLoader
                : FiClock;

    return (
        <span className={`mr-status-badge ${status || 'pending'}`}>
            <Icon className={status === 'ingesting' ? 'mr-spin' : ''}/>
            {ingestLabels[status] || status || ingestLabels.pending}
        </span>
    );
};

const GraphStatusBadge = ({status}) => {
    const Icon = status === 'ready' ? FiCheckCircle : status === 'error' ? FiAlertCircle : status === 'processing' ? FiLoader : FiClock;
    return <span className={`mr-status-badge graph-${status || 'pending'}`}><Icon className={status === 'processing' ? 'mr-spin' : ''}/>{graphLabels[status] || graphLabels.pending}</span>;
};

const ManageResources = () => {
    const {isPlatformAdmin} = useAuthRole();
    const [searchParams, setSearchParams] = useSearchParams();
    const [resources, setResources] = useState([]);
    const [topics, setTopics] = useState([]);
    const [courses, setCourses] = useState([]);
    const [subjectOptions, setSubjectOptions] = useState(defaultSubjectOptions);
    const [files, setFiles] = useState([]);
    const [topicIds, setTopicIds] = useState([]);
    const [courseId, setCourseId] = useState('');
    const [subjectType, setSubjectType] = useState('general');
    const [description, setDescription] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [organizationFilter, setOrganizationFilter] = useState('');
    const [courseFilter, setCourseFilter] = useState(searchParams.get('course') || '');
    const [topicFilter, setTopicFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [busyAction, setBusyAction] = useState('');
    const [message, setMessage] = useState(null);
    const manageableCourses = useMemo(() => courses.filter((course) => course.can_manage_content === true), [courses]);
    const courseById = useMemo(() => new Map(courses.map((course) => [String(course.id), course])), [courses]);
    const organizationOptions = useMemo(() => [...new Map(courses.filter((course) => course.organization_id != null).map((course) => [String(course.organization_id), {id: String(course.organization_id), name: course.organization_name || `مجموعه ${course.organization_id}`}])).values()], [courses]);
    const filterCourses = useMemo(() => courses.filter((course) => !organizationFilter || String(course.organization_id) === organizationFilter), [courses, organizationFilter]);
    const filterTopics = useMemo(() => topics.filter((topic) => {
        if (courseFilter) return String(topic.course_id) === courseFilter;
        if (organizationFilter) return String(courseById.get(String(topic.course_id))?.organization_id) === organizationFilter;
        return true;
    }), [topics, courseFilter, organizationFilter, courseById]);

    useEffect(() => {
        const requestedCourse = searchParams.get('course') || '';
        setCourseFilter(requestedCourse);
        if (requestedCourse) setOrganizationFilter(String(courseById.get(requestedCourse)?.organization_id || ''));
        if (requestedCourse && searchParams.get('upload') === '1' && manageableCourses.some((course) => String(course.id) === requestedCourse)) {
            setCourseId(requestedCourse);
            setShowModal(true);
            const next = new URLSearchParams(searchParams);
            next.delete('upload');
            setSearchParams(next, {replace: true});
        }
    }, [manageableCourses, courseById, searchParams, setSearchParams]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [documentItems, topicItems, courseItems, graphResponse] = await Promise.all([
                resourcesApi.listAll(),
                topicsApi.list('?archived=false'),
                accountsApi.courses.list(),
                knowledgeGraphApi.resources({limit: 100, offset: 0}).catch(() => ({results: []})),
            ]);
            const graphItems = normalizeCollection(graphResponse);
            const graphById = new Map(graphItems.map((item) => [String(item.resource_id), item]));
            setResources(normalizeCollection(documentItems).map((item) => ({
                ...item,
                graph: graphById.get(String(item.resource_id)) || null,
            })));
            setTopics(normalizeCollection(topicItems));
            setCourses(normalizeCollection(courseItems));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const loadSubjects = useCallback(async () => {
        try {
            const items = normalizeCollection(await subjectInstructionsApi.list({is_active: true}));
            const activeSubjects = items
                .filter((item) => item.is_active !== false)
                .map((item) => ({
                    subject_type: item.normalized_subject_type || item.subject_type,
                    title: item.title || item.normalized_subject_type || item.subject_type,
                }))
                .filter((item) => item.subject_type);
            if (activeSubjects.length) {
                setSubjectOptions(activeSubjects);
                setSubjectType((current) => activeSubjects.some((item) => item.subject_type === current)
                    ? current
                    : activeSubjects[0].subject_type);
            }
        } catch (error) {
            setSubjectOptions(defaultSubjectOptions);
        }
    }, []);

    useEffect(() => { loadSubjects(); }, [loadSubjects]);

    const filteredResources = useMemo(() => resources.filter((resource) => {
        const course = courseById.get(String(resource.course_id));
        const query = searchTerm.trim().toLocaleLowerCase('fa');
        const matchesSearch = !query || [resource.title, resource.original_name, course?.title, course?.organization_name]
            .filter(Boolean).some((value) => String(value).toLocaleLowerCase('fa').includes(query));
        return matchesSearch
            && (statusFilter === 'all' || resource.ingest_status === statusFilter)
            && (!organizationFilter || String(course?.organization_id) === organizationFilter)
            && (!courseFilter || String(resource.course_id) === courseFilter)
            && (!topicFilter || (resource.topic_ids || []).some((id) => String(id) === topicFilter));
    }), [resources, courseById, searchTerm, statusFilter, organizationFilter, courseFilter, topicFilter]);
    const availableTopics = useMemo(
        () => topics.filter((topic) => String(topic.course_id || '') === courseId),
        [courseId, topics],
    );
    const availableEditTopics = useMemo(() => {
        if (!editForm) return [];
        return topics.filter((topic) => String(topic.course_id || '') === editForm.course_id);
    }, [editForm, topics]);

    const stats = useMemo(() => ({
        all: resources.length,
        ready: resources.filter((resource) => resource.ingest_status === 'ingested').length,
        processing: resources.filter((resource) => ['pending', 'ingesting'].includes(resource.ingest_status)).length,
        failed: resources.filter((resource) => resource.ingest_status === 'failed').length,
    }), [resources]);

    const closeModal = () => {
        if (busyAction === 'upload') return;
        setShowModal(false);
        setFiles([]);
        setTopicIds([]);
        setCourseId('');
        setSubjectType(subjectOptions[0]?.subject_type || 'general');
        setDescription('');
    };

    const closeEditModal = () => {
        if (busyAction.startsWith('edit-')) return;
        setEditingResource(null);
        setEditForm(null);
    };

    const openEditModal = (resource) => {
        setMessage(null);
        setEditingResource(resource);
        setEditForm({
            title: resource.title || resource.original_name || '',
            description: resource.description || '',
            course_id: resource.course_id == null ? '' : String(resource.course_id),
            subject_type: resource.subject_type || subjectOptions[0]?.subject_type || 'general',
            resource_type: resource.resource_type || 'file',
            topic_ids: (resource.topic_ids || []).map(String),
        });
    };

    const handleEdit = async (event) => {
        event.preventDefault();
        if (!editingResource || !editForm) return;
        setBusyAction(`edit-${editingResource.resource_id}`);
        setMessage(null);
        try {
            await resourcesApi.update(editingResource.resource_id, {
                title: editForm.title.trim(),
                description: editForm.description.trim(),
                course_id: editForm.course_id ? Number(editForm.course_id) : null,
                subject_type: editForm.subject_type,
                resource_type: editForm.resource_type.trim(),
                topic_ids: editForm.topic_ids.map(Number),
            });
            setEditingResource(null);
            setEditForm(null);
            setMessage({type: 'success', text: 'اطلاعات و تخصیص‌های منبع با موفقیت ویرایش شد.'});
            await loadData();
        } catch (error) {
            setMessage({type: 'error', text: formatEditError(error)});
        } finally {
            setBusyAction('');
        }
    };

    const runAction = async (name, id, action, successText) => {
        setBusyAction(`${name}-${id}`);
        setMessage(null);
        try {
            const result = await action(id);
            setMessage({type: 'success', text: result?.task_id ? `${successText} شناسه task: ${result.task_id}` : successText});
            await loadData();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    const downloadFile = async (resource) => {
        setBusyAction(`file-${resource.resource_id}`);
        setMessage(null);
        try {
            const {blob, filename} = await resourcesApi.file(resource.resource_id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || resource.original_name || resource.title || 'resource';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    const handleUpload = async (event) => {
        event.preventDefault();
        if (!files.length) return;
        if (!subjectType) {
            setMessage({type: 'error', text: 'برای بارگذاری منبع، انتخاب نوع درس الزامی است.'});
            return;
        }
        if (!courseId || !manageableCourses.some((course) => String(course.id) === String(courseId))) {
            setMessage({type: 'error', text: 'برای بارگذاری منبع، انتخاب دوره الزامی است.'});
            return;
        }
        setBusyAction('upload');
        setMessage(null);
        try {
            const result = await resourcesApi.upload(files, {
                subject_type: subjectType,
                description: description.trim(),
                course_id: Number(courseId),
            });
            const assignments = topicIds.length
                ? await Promise.allSettled(result.items.flatMap((item) => topicIds.map((id) => topicsApi.addResource(id, item.resource_id))))
                : [];
            const failedAssignments = assignments.filter((assignment) => assignment.status === 'rejected').length;
            setMessage(failedAssignments
                ? {type: 'error', text: `${result.items.length} فایل بارگذاری شد، اما اتصال ${failedAssignments} موضوع انجام نشد. موضوع‌ها را از ویرایش منبع بررسی کنید.`}
                : {type: 'success', text: `${result.items.length} فایل با موفقیت بارگذاری شد.`});
            setFiles([]);
            setTopicIds([]);
            setCourseId('');
            setSubjectType(subjectOptions[0]?.subject_type || 'general');
            setDescription('');
            setShowModal(false);
            await loadData();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    const renderActionLabel = (name, id, label, Icon) => busyAction === `${name}-${id}`
        ? <><FiLoader className="mr-spin"/> در حال ثبت...</>
        : <><Icon/> {label}</>;

    return (
        <div className="mr-page">
            <section className="mr-hero app-page-header">
                <div>
                    <span className="mr-eyebrow"><FiBookOpen/> کتابخانه و گراف دانش</span>
                    <h1>منابع آموزشی</h1>
                    <p>هر منبع را یک‌بار بارگذاری کنید؛ پردازش متن، گراف دانش، تولید سؤال و طراحی آزمون از همین‌جا در دسترس‌اند.</p>
                </div>
                <div className="mr-hero-actions app-page-header-actions">
                    <Link className="mr-button ghost" to="/knowledge-graph/concepts">
                        <FiDatabase/> همه مفاهیم
                    </Link>
                    {manageableCourses.length > 0 && <button className="mr-button primary upload" type="button" onClick={() => setShowModal(true)}>
                        <FiPlus/> افزودن منبع جدید
                    </button>}
                </div>
            </section>

            <section className="mr-stats" aria-label="خلاصه منابع">
                <div className="mr-stat-card"><span className="mr-stat-icon blue"><FiLayers/></span><div><strong>{stats.all}</strong><span>کل منابع</span></div></div>
                <div className="mr-stat-card"><span className="mr-stat-icon green"><FiCheckCircle/></span><div><strong>{stats.ready}</strong><span>آماده تولید سؤال</span></div></div>
                <div className="mr-stat-card"><span className="mr-stat-icon amber"><FiClock/></span><div><strong>{stats.processing}</strong><span>در صف پردازش</span></div></div>
                <div className="mr-stat-card"><span className="mr-stat-icon red"><FiAlertCircle/></span><div><strong>{stats.failed}</strong><span>نیازمند بررسی</span></div></div>
            </section>

            {message && <div className={`mr-message ${message.type}`}>{message.text}</div>}

            <section className="mr-library">
                <div className="mr-library-header">
                    <div>
                        <h2>کتابخانه منابع</h2>
                        <p>{filteredResources.length} منبع نمایش داده می‌شود</p>
                    </div>
                    <button className="mr-refresh-button" type="button" onClick={loadData} disabled={isLoading}>
                        <FiRefreshCw className={isLoading ? 'mr-spin' : ''}/> بروزرسانی
                    </button>
                </div>

                <div className="mr-controls">
                    <label className="mr-search">
                        <FiSearch/>
                        <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="جستجو در عنوان منبع، دوره یا مجموعه..."/>
                    </label>
                    <div className="mr-filters">
                        {statusFilters.map((filter) => (
                            <button key={filter.value} type="button" className={statusFilter === filter.value ? 'active' : ''}
                                onClick={() => setStatusFilter(filter.value)}>{filter.label}</button>
                        ))}
                    </div>
                </div>
                <div className="mr-scope-filters" aria-label="فیلتر محل منبع">
                    <label>مجموعه
                        <select value={organizationFilter} onChange={(event) => { setOrganizationFilter(event.target.value); setCourseFilter(''); setTopicFilter(''); setSearchParams({}, {replace: true}); }}>
                            <option value="">همه مجموعه‌ها</option>
                            {organizationOptions.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                        </select>
                    </label>
                    <label>دوره
                        <select value={courseFilter} onChange={(event) => { const value = event.target.value; setCourseFilter(value); setTopicFilter(''); setSearchParams(value ? {course: value} : {}, {replace: true}); }}>
                            <option value="">همه دوره‌ها</option>
                            {filterCourses.map((course) => <option key={course.id} value={course.id}>{course.title} · {course.organization_name}</option>)}
                        </select>
                    </label>
                    <label>موضوع
                        <select value={topicFilter} onChange={(event) => setTopicFilter(event.target.value)}>
                            <option value="">همه موضوع‌ها</option>
                            {filterTopics.map((topic) => <option key={topic.id} value={topic.id}>{topic.main}</option>)}
                        </select>
                    </label>
                    {(organizationFilter || courseFilter || topicFilter || statusFilter !== 'all' || searchTerm) && <button type="button" onClick={() => { setOrganizationFilter(''); setCourseFilter(''); setTopicFilter(''); setStatusFilter('all'); setSearchTerm(''); setSearchParams({}, {replace: true}); }}>پاک کردن فیلترها</button>}
                </div>

                {isLoading ? (
                    <div className="mr-state"><FiLoader className="mr-state-icon mr-spin"/><h3>در حال دریافت منابع...</h3></div>
                ) : filteredResources.length ? (
                    <div className="mr-resource-grid">
                        {filteredResources.map((resource) => (
                            <article className="mr-resource-card" key={resource.resource_id}>
                                <div className="mr-card-heading">
                                    <div className="mr-file-icon"><FiFileText/><span>{getFileExtension(resource.original_name || resource.title)}</span></div>
                                    <div className="mr-file-info">
                                        <h3 title={resource.title || resource.original_name}>{resource.title || resource.original_name}</h3>
                                        <span>{formatFileSize(resource.size_bytes)}</span>
                                        {(resource.subject_title || resource.subject_type) && (
                                            <small className="mr-subject-label">
                                                {resource.subject_title || resource.subject_type}
                                            </small>
                                        )}
                                        {resource.course_id ? <Link className="mr-resource-path" to={`/organizations?organization=${encodeURIComponent(courseById.get(String(resource.course_id))?.organization_id || '')}&course=${encodeURIComponent(resource.course_id)}`}>
                                            {courseById.get(String(resource.course_id))?.organization_name || 'مجموعه'} / {courseById.get(String(resource.course_id))?.title || `دوره ${resource.course_id}`}
                                        </Link> : <small className="mr-resource-path">بدون دوره · منبع قدیمی</small>}
                                        {!!resource.topic_ids?.length && <small className="mr-resource-topics">موضوع‌ها: {resource.topic_ids.map((id) => topics.find((topic) => String(topic.id) === String(id))?.main).filter(Boolean).join('، ') || `${resource.topic_ids.length} موضوع`}</small>}
                                        {resource.description && <p>{resource.description}</p>}
                                        {getLlmLogId(resource, 'ingest') && <small className="mr-log-id">LLM log: {getLlmLogId(resource, 'ingest')}</small>}
                                    </div>
                                    <div className="mr-card-controls">
                                        {resource.can_edit && <button className="mr-icon-button edit" type="button" aria-label={`ویرایش ${resource.original_name || resource.title}`} disabled={!!busyAction}
                                            onClick={() => openEditModal(resource)}>
                                            <FiEdit3/>
                                        </button>}
                                        {resource.can_edit === true && <button className="mr-icon-button danger" type="button" aria-label={`حذف ${resource.original_name || resource.title}`} disabled={!!busyAction}
                                            onClick={() => window.confirm('منبع، گراف دانش و داده‌های پردازش‌شده حذف شوند؟') && runAction('delete', resource.resource_id, resourcesApi.remove, 'منبع حذف شد.')}>
                                            <FiTrash2/>
                                        </button>}
                                    </div>
                                </div>

                                <div className="mr-progress">
                                    <div className={`mr-progress-step ${resource.ingest_status === 'ingested' ? 'complete' : resource.ingest_status}`}>
                                        <span><FiCpu/></span><div><small>پردازش منبع و گراف</small><StatusBadge status={resource.ingest_status}/></div>
                                    </div>
                                    <div className={`mr-progress-step ${resource.graph?.status === 'ready' ? 'complete' : resource.graph?.status || 'pending'}`}>
                                        <span><FiGitBranch/></span><div><small>گراف دانش</small><GraphStatusBadge status={resource.graph?.status}/></div>
                                    </div>
                                </div>

                                {resource.graph && <details className="mr-graph-details"><summary>جزئیات گراف دانش</summary><div className="mr-graph-metrics"><span><strong>{resource.graph.active_chunk_count || 0}</strong> بخش</span><span><strong>{resource.graph.concept_count || 0}</strong> مفهوم</span><span>نسخه <strong>{resource.graph.version || 1}</strong></span></div></details>}

                                <div className="mr-card-actions">
                                    {resource.file_url && <button className="mr-button ghost" type="button" disabled={!!busyAction} onClick={() => downloadFile(resource)}><FiEye/> دریافت فایل</button>}
                                    {resource.can_edit === true && resource.ingest_status !== 'ingested' && <button className="mr-button soft-blue" type="button" disabled={!!busyAction || resource.ingest_status === 'ingesting'}
                                        onClick={() => runAction('ingest', resource.resource_id, resourcesApi.ingest, 'پردازش منبع و ساخت گراف دانش در صف قرار گرفت.')}>
                                        {renderActionLabel('ingest', resource.resource_id, 'پردازش و ساخت گراف', FiPlay)}
                                    </button>}
                                    {(resource.graph || resource.ingest_status === 'ingested') && <Link className="mr-button soft-green" to={`/knowledge-graph/${encodeURIComponent(resource.resource_id)}`}><FiGitBranch/> {resource.graph?.status === 'ready' ? 'مشاهده گراف' : 'وضعیت گراف'}</Link>}
                                    <Link className="mr-button ghost" to={`/questionBank?resource=${encodeURIComponent(resource.resource_id)}`}><FiDatabase/> سؤال‌ها</Link>
                                    {isPlatformAdmin && <Link className="mr-button ghost" to={`/questionBank?resource=${encodeURIComponent(resource.resource_id)}&report=1`}><FiFileText/> گزارش پژوهشی</Link>}
                                    {isPlatformAdmin && resource.can_edit === true && <Link className="mr-button ghost" to={`/exam-planner?resource=${encodeURIComponent(resource.resource_id)}`}><FiBookOpen/> طراحی آزمون</Link>}
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="mr-state">
                        <span className="mr-empty-icon"><FiDatabase/></span>
                        <h3>{resources.length ? 'منبعی با این مشخصات پیدا نشد' : 'کتابخانه هنوز خالی است'}</h3>
                        <p>{resources.length ? 'عبارت جستجو یا فیلتر وضعیت را تغییر دهید.' : 'اولین فایل آموزشی را اضافه کنید تا روند آماده‌سازی سؤال شروع شود.'}</p>
                        {!resources.length && manageableCourses.length > 0 && <button className="mr-button primary" type="button" onClick={() => setShowModal(true)}><FiUploadCloud/> بارگذاری اولین منبع</button>}
                    </div>
                )}
            </section>

            {editingResource && editForm && <div className="mr-modal-overlay" onClick={closeEditModal}>
                <div className="mr-modal mr-edit-modal" role="dialog" aria-modal="true" aria-labelledby="edit-resource-title" onClick={(event) => event.stopPropagation()}>
                    <div className="mr-modal-header">
                        <div><span className="mr-modal-icon"><FiEdit3/></span><div><h2 id="edit-resource-title">ویرایش منبع</h2><p>{editingResource.original_name || editingResource.resource_id}</p></div></div>
                        <button type="button" className="mr-icon-button" onClick={closeEditModal} aria-label="بستن"><FiX/></button>
                    </div>
                    <form onSubmit={handleEdit}>
                        <div className="mr-modal-body">
                            <div className="mr-edit-grid">
                                <label className="mr-field">
                                    <span>عنوان <small>الزامی</small></span>
                                    <input required value={editForm.title} onChange={(event) => setEditForm({...editForm, title: event.target.value})}/>
                                </label>
                                <label className="mr-field">
                                    <span>نوع منبع <small>الزامی</small></span>
                                    <input required value={editForm.resource_type} onChange={(event) => setEditForm({...editForm, resource_type: event.target.value})} placeholder="file، handout، lesson..."/>
                                </label>
                            </div>
                            <label className="mr-field">
                                <span>توضیحات</span>
                                <textarea rows="3" value={editForm.description} onChange={(event) => setEditForm({...editForm, description: event.target.value})}/>
                            </label>
                            <div className="mr-edit-grid">
                                <label className="mr-field">
                                    <span>دوره</span>
                                    <select value={editForm.course_id} onChange={(event) => setEditForm({...editForm, course_id: event.target.value, topic_ids: []})}>
                                        <option value="" disabled={Boolean(editingResource.course_id)}>انتخاب دوره</option>
                                        {courses.filter((course) => (course.can_manage_content && course.is_active !== false) || String(course.id) === editForm.course_id).map((course) => <option key={course.id} value={course.id}>{course.title}{course.organization_name ? ` · ${course.organization_name}` : ''}</option>)}
                                    </select>
                                </label>
                                <label className="mr-field">
                                    <span>نوع درس</span>
                                    <select required value={editForm.subject_type} onChange={(event) => setEditForm({...editForm, subject_type: event.target.value})}>
                                        {subjectOptions.map((subject) => <option key={subject.subject_type} value={subject.subject_type}>{subject.title || subject.subject_type}</option>)}
                                    </select>
                                </label>
                            </div>
                            <fieldset className="mr-topic-picker">
                                <legend>موضوع‌های منبع</legend>
                                {availableEditTopics.length ? <div>
                                    {availableEditTopics.map((topic) => {
                                        const id = String(topic.id);
                                        const checked = editForm.topic_ids.includes(id);
                                        return <label key={topic.id} className={checked ? 'selected' : ''}>
                                            <input type="checkbox" checked={checked} onChange={() => setEditForm((current) => ({
                                                ...current,
                                                topic_ids: checked ? current.topic_ids.filter((item) => item !== id) : [...current.topic_ids, id],
                                            }))}/>
                                            <span>{topic.main}</span>
                                        </label>;
                                    })}
                                </div> : <p>{editForm.course_id ? 'برای این دوره موضوع فعالی ثبت نشده است.' : 'موضوع‌های عمومی فعالی ثبت نشده است.'}</p>}
                            </fieldset>
                            <p className="mr-topic-required-note">هر منبع به یک دوره تعلق دارد و می‌تواند چند موضوع داشته باشد. منابع قدیمی بدون دوره را هنگام ویرایش به یک دوره وصل کنید. انتقال دوره فقط زمانی انجام می‌شود که سؤال یا آزمون ناسازگار وابسته نباشد.</p>
                        </div>
                        <div className="mr-modal-actions">
                            <button type="button" className="mr-button ghost" onClick={closeEditModal}>انصراف</button>
                            <button className="mr-button primary" disabled={!editForm.title.trim() || !editForm.resource_type.trim() || !editForm.subject_type || !editForm.course_id || busyAction === `edit-${editingResource.resource_id}`}>
                                {busyAction === `edit-${editingResource.resource_id}` ? <><FiLoader className="mr-spin"/> در حال ذخیره...</> : <><FiCheckCircle/> ذخیره تغییرات</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>}

            {showModal && <div className="mr-modal-overlay" onClick={closeModal}>
                <div className="mr-modal" role="dialog" aria-modal="true" aria-labelledby="upload-title" onClick={(event) => event.stopPropagation()}>
                    <div className="mr-modal-header">
                        <div><span className="mr-modal-icon"><FiUploadCloud/></span><div><h2 id="upload-title">افزودن منبع جدید</h2><p>فایل‌های آموزشی موردنظر را انتخاب کنید.</p></div></div>
                        <button type="button" className="mr-icon-button" onClick={closeModal} aria-label="بستن"><FiX/></button>
                    </div>
                    <form onSubmit={handleUpload}>
                        <div className="mr-modal-body">
                            <label className="mr-dropzone">
                                <FiUploadCloud/>
                                <strong>{files.length ? `${files.length} فایل انتخاب شده است` : 'فایل‌ها را انتخاب کنید'}</strong>
                                <span>PDF، TXT، MD، DOCX یا PPTX تا سقف ۵۰ مگابایت</span>
                                <input type="file" multiple required accept=".pdf,.txt,.md,.docx,.pptx" onChange={(event) => setFiles(Array.from(event.target.files))}/>
                            </label>
                            {files.length > 0 && <div className="mr-selected-files">
                                {files.map((file) => <span key={`${file.name}-${file.size}`}><FiFileText/>{file.name}<small>{formatFileSize(file.size)}</small></span>)}
                            </div>}
                            <label className="mr-field">
                                <span>دوره <small>الزامی</small></span>
                                <select required value={courseId} onChange={(event) => { setCourseId(event.target.value); setTopicIds([]); }}>
                                    <option value="">انتخاب دوره...</option>
                                    {manageableCourses.filter((course) => course.is_active !== false).map((course) => <option key={course.id} value={course.id}>{course.title} · {course.organization_name}</option>)}
                                </select>
                            </label>
                            <label className="mr-field">
                                <span>نوع درس <small>الزامی</small></span>
                                <select required value={subjectType} onChange={(event) => setSubjectType(event.target.value)}>
                                    {subjectOptions.map((subject) => (
                                        <option key={subject.subject_type} value={subject.subject_type}>
                                            {subject.title || subject.subject_type}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="mr-field">
                                <span>توضیح منبع <small>اختیاری</small></span>
                                <textarea
                                    rows="3"
                                    value={description}
                                    onChange={(event) => setDescription(event.target.value)}
                                    placeholder="مثلاً: جزوه فصل دوم برای تولید سؤال"
                                />
                            </label>
                            <fieldset className="mr-topic-picker">
                                <legend>موضوع‌های منبع <small>اختیاری</small></legend>
                                {availableTopics.length ? <div>{availableTopics.map((topic) => {
                                    const id = String(topic.id);
                                    const checked = topicIds.includes(id);
                                    return <label key={id} className={checked ? 'selected' : ''}><input type="checkbox" checked={checked} onChange={() => setTopicIds((current) => checked ? current.filter((item) => item !== id) : [...current, id])}/><span>{topic.main}</span></label>;
                                })}</div> : <p>برای این دوره موضوعی ثبت نشده است.</p>}
                            </fieldset>
                            <p className={`mr-topic-required-note ${courseId && !availableTopics.length ? 'error' : ''}`}>
                                {availableTopics.length
                                    ? 'فقط موضوع‌های متعلق به دوره انتخاب‌شده نمایش داده می‌شوند.'
                                    : courseId ? 'این دوره هنوز موضوعی ندارد؛ می‌توانید منبع را بدون موضوع بارگذاری کنید.' : 'ابتدا دوره را انتخاب کنید.'}
                            </p>
                        </div>
                        <div className="mr-modal-actions">
                            <button type="button" className="mr-button ghost" onClick={closeModal}>انصراف</button>
                            <button className="mr-button primary" disabled={!files.length || !courseId || !subjectType || busyAction === 'upload'}>
                                {busyAction === 'upload' ? <><FiLoader className="mr-spin"/> در حال بارگذاری...</> : <><FiUploadCloud/> بارگذاری منابع</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>}
        </div>
    );
};

export default ManageResources;
